import { FlaskConical } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  EmptyState,
  ErrorState,
  LoadingState,
  type BadgeTone,
} from '@/components/ui';
import type { LabOrder, LabOrderStatus } from '@/features/orders/types';
import styles from './LabOrderHandoff.module.css';

const STATUS_TONE: Record<LabOrderStatus, BadgeTone> = {
  draft: 'warning',
  ordered: 'info',
  collected: 'info',
  dispatched: 'info',
  results_ready: 'warning',
  reviewed: 'success',
  cancelled: 'danger',
};

interface LabOrderHandoffProps {
  orders: LabOrder[] | undefined;
  isLoading: boolean;
  error: unknown;
  onRetry: () => void;
}

/** Read-only lab handoff shown with the clinical summary of a closed visit. */
export function LabOrderHandoff({
  orders,
  isLoading,
  error,
  onRetry,
}: LabOrderHandoffProps) {
  const { t } = useTranslation();
  const statusLabel: Record<LabOrderStatus, string> = {
    draft: t('consultation.labOrderStatusDraft'),
    ordered: t('consultation.labOrderStatusOrdered'),
    collected: t('consultation.labOrderStatusCollected'),
    dispatched: t('consultation.labOrderStatusDispatched'),
    results_ready: t('consultation.labOrderStatusResultsReady'),
    reviewed: t('consultation.labOrderStatusReviewed'),
    cancelled: t('consultation.labOrderStatusCancelled'),
  };

  return (
    <section
      className={styles.section}
      aria-label={t('consultation.labOrders')}
    >
      <div className={styles.heading}>
        <FlaskConical size={16} aria-hidden="true" />
        <p className="eyebrow">{t('consultation.labOrders')}</p>
      </div>
      <p className={styles.intro}>{t('consultation.labOrdersSummary')}</p>

      {isLoading && <LoadingState label={t('consultation.labOrdersLoading')} />}
      {error != null && (
        <ErrorState
          error={error}
          title={t('consultation.labOrdersErrorTitle')}
          body={t('consultation.labOrdersErrorBody')}
          onRetry={onRetry}
        />
      )}
      {!isLoading && error == null && orders?.length === 0 && (
        <EmptyState
          icon={<FlaskConical size={22} />}
          title={t('consultation.labOrdersEmptyTitle')}
          body={t('consultation.labOrdersEmptyBody')}
        />
      )}
      {!isLoading && error == null && orders && orders.length > 0 && (
        <ul className={styles.orders}>
          {orders.map((order) => (
            <li key={order.id} className={styles.order}>
              <div className={styles.orderHeading}>
                <strong className={styles.number}>
                  {order.number ?? t('consultation.labOrderPendingNumber')}
                </strong>
                <Badge tone={STATUS_TONE[order.status]}>
                  {statusLabel[order.status]}
                </Badge>
              </div>
              <p className={styles.tests}>
                {t('consultation.labOrderTests', {
                  tests: order.tests.map((test) => test.testName).join(', '),
                })}
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
